/**
 * React Router configuration
 * Defines all routes matching the Next.js pages structure
 */
import { createBrowserRouter, Navigate } from 'react-router-dom';
import MyApp from './pages/_app.tsx';

// Lazy load page components for code splitting
import { supportedLanguages } from '@plato/app-meta/locale';
import { lazy } from 'react';
import {
    PlatoUserLayout,
    type RouteHandle,
} from './features/plato-user-layout/components/plato-user-layout.tsx';
import { LanguageRedirect } from './language-redirect.tsx';
import { AuthenticatedLayout } from './layouts/authenticated-layout.tsx';
import { SuperAdminLayout } from './layouts/super-admin-layout.tsx';

// ============================================================================
// AUTHENTICATION PAGES (public)
// ============================================================================
const SignInPage = lazy(() => import('./pages/sign-in/[[...index]].tsx'));

// ============================================================================
// PUBLIC PAGES
// ============================================================================
const NotFoundPage = lazy(() => import('./pages/404.tsx'));

// ============================================================================
// PROTECTED PAGES (require authentication)
// ============================================================================
const HomePage = lazy(() => import('./pages/home/index.tsx'));
const CustomerListView = lazy(async () => {
    const { CustomerListView } = await import('@plato/feature-customer-list/components/customer-list-view');
    return { default: CustomerListView };
});
const CustomerDetailPage = lazy(() => import('./pages/customers/[customer_id]/index.tsx'));
const CustomerInsightsPage = lazy(() => import('./pages/customers/[customer_id]/insights.tsx'));
const CustomerInfoPage = lazy(() => import('./pages/customers/[customer_id]/info.tsx'));
const DraftsListPage = lazy(() => import('./pages/drafts/index.tsx'));
const DraftHeaderPage = lazy(() => import('./pages/drafts/[draft_id]/header.tsx'));
const DraftLineItemsPage = lazy(() => import('./pages/drafts/[draft_id]/line-items.tsx'));
const QuotesListPage = lazy(() => import('./pages/quotes/index.tsx'));
const QuoteDetailPage = lazy(() => import('./pages/quotes/[quote_id]/index.tsx'));
const OrdersListPage = lazy(() => import('./pages/orders/index.tsx'));
const OrderDetailPage = lazy(() => import('./pages/orders/[order_id]/index.tsx'));
const TasksPage = lazy(() => import('./pages/tasks/index.tsx'));
const EventsPage = lazy(() => import('./pages/events/index.tsx'));
const InventoryPage = lazy(() => import('./pages/inventory/[product-id].tsx'));
const TapiIntegrationPage = lazy(() => import('./pages/integrations/tapi/[phone].tsx'));
const ExplorerPage = lazy(() => import('./pages/explorer.tsx'));
const TeamPage = lazy(() => import('./pages/team.tsx'));
const TeamStructurePage = lazy(async () => {
    const { TeamStructurePage } = await import('@plato/feature-team-structure/pages/team-structure-page');
    return { default: TeamStructurePage };
});
const ActivitiesPage = lazy(() => import('./pages/activities/index.tsx'));
const CampaignListPage = lazy(async () => {
    const { CampaignListPage } = await import('@plato/feature-campaign/pages/campaign-list-page');
    return { default: CampaignListPage };
});

// Admin pages (use SuperAdminLayout via nested routes)
const AdminDashboardPage = lazy(() => import('./pages/admin/index.tsx'));
const AdminUsersPage = lazy(() => import('./pages/admin/admin-users.tsx'));
const AdminToolsPage = lazy(() => import('./pages/admin/tools.tsx'));
const AdminCustomersListPage = lazy(() => import('./pages/admin/customers/index.tsx'));
const AdminCustomerDetailPage = lazy(() => import('./pages/admin/customers/[customer_id]/index.tsx'));
const AdminCustomerDetailsPage = lazy(() => import('./pages/admin/customers/[customer_id]/details.tsx'));
const AdminCustomerUserCountPage = lazy(() => import('./pages/admin/customers/[customer_id]/user-count.tsx'));

export const router = createBrowserRouter([
    ...supportedLanguages.flatMap((language) =>
        [`/${language}/*`, `/${language}`].map((path) => ({
            path,
            element: <LanguageRedirect urlLanguage={language} />,
        })),
    ),
    // Main app routes
    {
        path: '/',
        element: <MyApp />,
        errorElement: <NotFoundPage />,
        children: [
            {
                index: true,
                element: <Navigate to="home" replace />,
            },

            // ================================================================
            // PUBLIC ROUTES
            // ================================================================
            {
                path: 'sign-in/*',
                element: <SignInPage />,
            },

            // ================================================================
            // PROTECTED ROUTES (require authentication)
            // ================================================================
            {
                element: <AuthenticatedLayout />,
                children: [
                    {
                        element: <PlatoUserLayout />,
                        children: [
                            {
                                path: 'home',
                                element: <HomePage />,
                            },
                            {
                                path: 'customers',
                                children: [
                                    {
                                        index: true,
                                        element: <CustomerListView />,
                                    },
                                    {
                                        path: ':customer_id',
                                        children: [
                                            {
                                                index: true,
                                                element: <CustomerDetailPage />,
                                            },
                                            {
                                                path: 'insights',
                                                element: <CustomerInsightsPage />,
                                            },
                                            {
                                                path: 'info',
                                                element: <CustomerInfoPage />,
                                            },
                                            {
                                                path: 'activity',
                                                element: <Navigate to=".." replace />,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'quotes',
                                children: [
                                    {
                                        index: true,
                                        element: <QuotesListPage />,
                                    },
                                    {
                                        path: ':quote_id',
                                        element: <QuoteDetailPage />,
                                    },
                                ],
                            },
                            {
                                path: 'orders',
                                children: [
                                    {
                                        index: true,
                                        element: <OrdersListPage />,
                                    },
                                    {
                                        path: ':order_id',
                                        element: <OrderDetailPage />,
                                    },
                                ],
                            },
                            {
                                path: 'drafts',
                                children: [
                                    {
                                        index: true,
                                        element: <DraftsListPage />,
                                    },
                                    {
                                        path: ':draft_id',
                                        children: [
                                            {
                                                path: 'line-items',
                                                element: <DraftLineItemsPage />,
                                                /**
                                                 * We need to disable the global dialogs because the line
                                                 * items page has its own drawers and they conflict with them
                                                 * */
                                                handle: {
                                                    disableDialogs: true,
                                                } satisfies RouteHandle,
                                            },
                                            {
                                                path: 'header',
                                                element: <DraftHeaderPage />,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'campaigns',
                                children: [
                                    {
                                        index: true,
                                        element: <CampaignListPage />,
                                    },
                                ],
                            },
                            {
                                path: 'tasks',
                                element: <TasksPage />,
                            },
                            {
                                path: 'events',
                                element: <EventsPage />,
                            },
                            {
                                path: 'inventory/:product_id',
                                element: <InventoryPage />,
                            },
                            {
                                path: 'integrations/tapi/*',
                                element: <TapiIntegrationPage />,
                            },
                            {
                                path: 'explorer',
                                element: <ExplorerPage />,
                            },
                            {
                                path: 'team',
                                element: <TeamPage />,
                            },
                            {
                                path: 'team-structure',
                                element: <TeamStructurePage />,
                            },
                            {
                                path: 'activities',
                                element: <ActivitiesPage />,
                            },
                        ],
                    },

                    // Admin routes (wrapped in SuperAdminLayout)
                    {
                        path: 'admin',
                        element: <SuperAdminLayout />,
                        children: [
                            {
                                index: true,
                                element: <AdminDashboardPage />,
                            },
                            {
                                path: 'admin-users',
                                element: <AdminUsersPage />,
                            },
                            {
                                path: 'customers',
                                children: [
                                    {
                                        index: true,
                                        element: <AdminCustomersListPage />,
                                    },
                                    {
                                        path: ':customer_id',
                                        children: [
                                            {
                                                index: true,
                                                element: <AdminCustomerDetailPage />,
                                            },
                                            {
                                                path: 'details',
                                                element: <AdminCustomerDetailsPage />,
                                            },
                                            {
                                                path: 'user-count',
                                                element: <AdminCustomerUserCountPage />,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'tools',
                                element: <AdminToolsPage />,
                            },
                        ],
                    },
                ],
            },

            // ================================================================
            // 404 - Catch all
            // ================================================================
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
    },
]);
